<template>
  <!-- Loading State (only during initial load) -->
  <div v-if="isLoading && isInitialLoad" class="auth-loading">
    <div class="loading-container">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-3 text-muted">Checking authentication...</p>
    </div>
  </div>

  <!-- Authentication Required -->
  <div v-else-if="!isAuthenticated" class="auth-required">
    <div class="welcome-container">
      <!-- Hero Section -->
      <div class="welcome-hero">
        <div class="hero-background">
          <div class="gradient-orb orb-1"></div>
          <div class="gradient-orb orb-2"></div>
          <div class="gradient-orb orb-3"></div>
        </div>
        <div class="hero-content">
          <div class="app-logo">
            <i class="fa-solid fa-wallet"></i>
          </div>
          <h1 class="welcome-title">Finly</h1>
          <p class="welcome-subtitle">Smart daily budgeting made simple</p>
          <div class="hero-stats">
            <div class="stat-item">
              <i class="fa-solid fa-lock-open"></i>
              <span>No Bank Connection Required</span>
            </div>
            <div class="stat-item">
              <i class="fa-solid fa-shield-halved"></i>
              <span>100% Secure</span>
            </div>
            <div class="stat-item">
              <i class="fa-solid fa-bolt"></i>
              <span>Simple</span>
            </div>
          </div>
          
          <button class="btn btn-primary btn-lg hero-cta-button" @click="handleAuthModalOpen">
            <span class="button-content">
              <i class="fa-solid fa-rocket me-2"></i>
              Get Started
            </span>
            <span class="button-shine"></span>
          </button>
        </div>
      </div>

      <!-- Mission Statement -->
      <div class="mission-statement">
        <div class="mission-content">
          <h2 class="mission-title">Made for the Everyday</h2>
          <p class="mission-text">
            We created Finly for everyday people who struggle with the complexity of finance tracking.
            If you're weary of connecting bank accounts to third-party sources, or simply want a tailored
            daily spending limit that's easy to follow—this is for you. Finly promotes healthy spending habits
            and gives you the financial comfort of knowing you're on the right track, without the stress.
          </p>
        </div>
      </div>

      <!-- Features Grid -->
      <div class="features-grid">
        <div class="feature-card" style="--card-color: #36A2EB;">
          <div class="feature-icon">
            <i class="fa-solid fa-calendar-day"></i>
          </div>
          <h3 class="feature-title">Daily Allowance</h3>
          <p class="feature-description">Automatically calculates your daily spending budget based on monthly income and
            expenses</p>
        </div>

        <div class="feature-card" style="--card-color: #51cf66;">
          <div class="feature-icon">
            <i class="fa-solid fa-heart-pulse"></i>
          </div>
          <h3 class="feature-title">Healthy Spending Habits</h3>
          <p class="feature-description">Gentle guidance toward controlled spending without judgment—build financial
            wellness one day at a time</p>
        </div>

        <div class="feature-card" style="--card-color: #FF6384;">
          <div class="feature-icon">
            <i class="fa-solid fa-chart-line"></i>
          </div>
          <h3 class="feature-title">Visual Insights</h3>
          <p class="feature-description">Track spending patterns with interactive charts and real-time budget updates
          </p>
        </div>

        <div class="feature-card" style="--card-color: #4BC0C0;">
          <div class="feature-icon">
            <i class="fa-solid fa-piggy-bank"></i>
          </div>
          <h3 class="feature-title">Savings Goals</h3>
          <p class="feature-description">Set and monitor savings goals with progress tracking and milestone celebrations
          </p>
        </div>

        <div class="feature-card" style="--card-color: #FFCE56;">
          <div class="feature-icon">
            <i class="fa-solid fa-tags"></i>
          </div>
          <h3 class="feature-title">Smart Categories</h3>
          <p class="feature-description">Automatic transaction categorization with customizable keywords and spending
            limits</p>
        </div>

        <div class="feature-card" style="--card-color: #9966FF;">
          <div class="feature-icon">
            <i class="fa-solid fa-message"></i>
          </div>
          <h3 class="feature-title">Natural Language Input</h3>
          <p class="feature-description">Add transactions and budgets using plain English—no forms or complicated fields
            required</p>
        </div>

        <div class="feature-card" style="--card-color: #FF9F40;">
          <div class="feature-icon">
            <i class="fa-solid fa-moon"></i>
          </div>
          <h3 class="feature-title">Dark Mode</h3>
          <p class="feature-description">Beautiful light and dark themes that adapt to your preference</p>
        </div>
      </div>

      <!-- Call to Action -->
      <div class="welcome-cta">
        <button class="btn btn-primary btn-lg cta-button" @click="handleAuthModalOpen">
          <span class="button-content">
            <i class="fa-solid fa-rocket me-2"></i>
            Get Started
          </span>
          <span class="button-shine"></span>
        </button>
        <p class="cta-note">
          <i class="fa-solid fa-shield-halved me-1"></i>
          Your data is securely stored in the cloud and never sold or shared
        </p>
      </div>
    </div>

    <!-- Auth Modal (shown when user clicks Get Started) -->
    <AuthModal :show="showAuthModal" @close="handleAuthModalClose" @authenticated="handleAuthenticated" />
  </div>

  <!-- Authenticated Content -->
  <div v-else>
    <slot />
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useAuth } from '../composables/useAuth';
import AuthModal from './AuthModal.vue';

export default {
  name: 'AuthGuard',
  components: {
    AuthModal
  },
  emits: ['authenticated'],
  setup(props, { emit }) {
    const { isAuthenticated, isLoading, getCurrentUser } = useAuth();
    const showAuthModal = ref(false);
    const isInitialLoad = ref(true); // Track if this is the initial authentication check

    const handleAuthenticated = () => {
      showAuthModal.value = false;
      isInitialLoad.value = false;
      emit('authenticated');
    };

    const handleAuthModalOpen = () => {
      showAuthModal.value = true;
      // Modal will start directly in auth flow (sign-in form)
    };

    const handleAuthModalClose = () => {
      showAuthModal.value = false;
    };

    onMounted(async () => {
      // Check if user is already authenticated
      await getCurrentUser();
      isInitialLoad.value = false;
    });

    return {
      isAuthenticated,
      isLoading,
      showAuthModal,
      isInitialLoad,
      handleAuthenticated,
      handleAuthModalOpen,
      handleAuthModalClose
    };
  }
};
</script>

<style scoped>
/* Prevent text selection on entire page */
.auth-loading,
.auth-loading *,
.auth-required,
.auth-required * {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.auth-loading {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--light-bg);
  overflow: hidden;
  cursor: default;
}

.loading-container {
  text-align: center;
  cursor: default;
}

.auth-required {
  background: var(--light-bg);
  padding: 2rem;
  overflow-y: auto;
  display: flex;
  justify-content: center;
  cursor: default;
}

@media (max-width: 768px) {
  .auth-required {
    top: 120px;
  }
}

.welcome-container {
  max-width: 1200px;
  width: 100%;
  animation: fadeInUp 0.6s ease-out;
  padding-top: 0.3rem;
  cursor: default;
}

/* Hero Section */
.welcome-hero {
  position: relative;
  text-align: center;
  margin-bottom: 4rem;
  padding: 4rem 2rem;
  overflow: hidden;
  border-radius: 20px;
}

.hero-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  z-index: 0;
}

.gradient-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.6;
}

.orb-1 {
  width: 400px;
  height: 400px;
  background: linear-gradient(135deg, #36A2EB, #4BC0C0);
  top: -200px;
  left: -100px;
  animation: float1 20s ease-in-out infinite;
}

.orb-2 {
  width: 350px;
  height: 350px;
  background: linear-gradient(135deg, #FF6384, #FF9F40);
  top: -100px;
  right: -100px;
  animation: float2 18s ease-in-out infinite;
}

.orb-3 {
  width: 300px;
  height: 300px;
  background: linear-gradient(135deg, #9966FF, #4BC0C0);
  bottom: -150px;
  left: 50%;
  animation: float3 22s ease-in-out infinite;
}

@keyframes float1 {
  0%, 100% {
    transform: translate(0, 0) scale(1);
  }
  33% {
    transform: translate(30px, -30px) scale(1.1);
  }
  66% {
    transform: translate(-20px, 20px) scale(0.9);
  }
}

@keyframes float2 {
  0%, 100% {
    transform: translate(0, 0) scale(1);
  }
  33% {
    transform: translate(-25px, 25px) scale(1.05);
  }
  66% {
    transform: translate(20px, -15px) scale(0.95);
  }
}

@keyframes float3 {
  0%, 100% {
    transform: translateX(-50%) translate(0, 0) scale(1);
  }
  33% {
    transform: translateX(-50%) translate(15px, -20px) scale(1.08);
  }
  66% {
    transform: translateX(-50%) translate(-30px, 15px) scale(0.92);
  }
}

.hero-content {
  position: relative;
  z-index: 1;
}

.app-logo {
  width: 120px;
  height: 120px;
  background: linear-gradient(135deg, #36A2EB, #4BC0C0);
  border-radius: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 2rem;
  box-shadow: 0 20px 60px rgba(54, 162, 235, 0.4);
  animation: logoFloat 3s ease-in-out infinite;
  position: relative;
}

.app-logo::before {
  content: '';
  position: absolute;
  inset: -3px;
  background: linear-gradient(135deg, #36A2EB, #4BC0C0, #FF6384);
  border-radius: 30px;
  z-index: -1;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.app-logo:hover::before {
  opacity: 1;
  animation: rotate 3s linear infinite;
}

@keyframes logoFloat {

  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-10px);
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.app-logo i {
  font-size: 3.5rem;
  color: white;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));
}

.welcome-title {
  font-size: 3.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, #36A2EB, #4BC0C0, #FF6384);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 1rem;
  animation: fadeInUp 0.8s ease-out 0.2s both;
  cursor: default;
  user-select: none;
}

.welcome-subtitle {
  font-size: 1.4rem;
  color: var(--light-text-muted);
  margin: 0 0 2rem 0;
  animation: fadeInUp 0.8s ease-out 0.4s both;
  cursor: default;
  user-select: none;
}

.hero-stats {
  display: flex;
  justify-content: center;
  gap: 2rem;
  flex-wrap: wrap;
  animation: fadeInUp 0.8s ease-out 0.6s both;
  margin-bottom: 3.5rem;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, rgba(54, 162, 235, 0.1), rgba(75, 192, 192, 0.1));
  border: none;
  border-radius: 50px;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--light-text);
  box-shadow: none;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.stat-item::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(54, 162, 235, 0.15), rgba(75, 192, 192, 0.15));
  opacity: 0;
  transition: opacity 0.3s ease;
}

.stat-item:hover::before {
  opacity: 1;
}

.stat-item:hover {
  transform: none;
}

.stat-item i {
  font-size: 1.1rem;
  color: #36A2EB;
  position: relative;
  z-index: 1;
}

.stat-item span {
  position: relative;
  z-index: 1;
}

/* Hero CTA Button */
.hero-cta-button {
  min-height: 56px;
  padding: 0.875rem 3rem;
  font-size: 1.1rem;
  font-weight: 700;
  border-radius: 50px;
  background: linear-gradient(135deg, #36A2EB, #4BC0C0);
  border: none;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 6px 25px rgba(54, 162, 235, 0.35);
  animation: fadeInUp 0.8s ease-out 0.8s both;
}

.hero-cta-button:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 35px rgba(54, 162, 235, 0.45);
}

.hero-cta-button:active {
  transform: translateY(-1px);
}

.hero-cta-button:hover .button-shine {
  left: 100%;
}

/* Mission Statement */
.mission-statement {
  margin-bottom: 4rem;
  animation: fadeInUp 0.8s ease-out 0.7s both;
}

.mission-content {
  max-width: 900px;
  margin: 0 auto;
  text-align: center;
  padding: 4rem 3rem;
  background: transparent;
  border: none;
  position: relative;
  overflow: visible;
}

.mission-content::before {
  content: '';
  position: absolute;
  inset: -2px;
  background: linear-gradient(135deg, #36A2EB, #4BC0C0, #FF6384, #FFCE56);
  border-radius: 30px;
  z-index: -2;
  opacity: 0.15;
  filter: blur(20px);
}

.mission-content::after {
  content: '';
  position: absolute;
  inset: 0;
  background: var(--light-card);
  border-radius: 30px;
  z-index: -1;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
}

.mission-title {
  font-size: 2.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, #36A2EB, #4BC0C0, #FF6384);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 2rem;
  position: relative;
  letter-spacing: -0.5px;
}

.mission-title::after {
  content: '';
  display: block;
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, #36A2EB, #4BC0C0);
  margin: 1.5rem auto 0;
  border-radius: 2px;
  box-shadow: 0 2px 10px rgba(54, 162, 235, 0.3);
}

.mission-text {
  font-size: 1.15rem;
  line-height: 2;
  color: var(--light-text);
  margin: 0;
  font-weight: 400;
  max-width: 750px;
  margin: 0 auto;
  position: relative;
}

.mission-text::before,
.mission-text::after {
  content: '"';
  font-size: 4rem;
  font-weight: 700;
  color: #36A2EB;
  opacity: 0.15;
  position: absolute;
  font-family: Georgia, serif;
  line-height: 1;
}

.mission-text::before {
  top: -20px;
  left: -30px;
}

.mission-text::after {
  bottom: -40px;
  right: -30px;
  transform: rotate(180deg);
}

/* Features Grid */
.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
}

.feature-card {
  background: var(--light-card);
  border-radius: 20px;
  padding: 2rem;
  text-align: center;
  transition: all 0.3s ease;
  box-shadow: none;
  position: relative;
  overflow: hidden;
  animation: fadeInUp 0.6s ease-out both;
}

.feature-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--card-color), transparent);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.feature-card:hover {
  transform: translateY(-8px);
  box-shadow: none;
  border-color: var(--card-color);
}

.feature-card:hover::before {
  opacity: 1;
}

.feature-card:nth-child(1) {
  animation-delay: 0.1s;
}

.feature-card:nth-child(2) {
  animation-delay: 0.2s;
}

.feature-card:nth-child(3) {
  animation-delay: 0.3s;
}

.feature-card:nth-child(4) {
  animation-delay: 0.4s;
}

.feature-card:nth-child(5) {
  animation-delay: 0.5s;
}

.feature-card:nth-child(6) {
  animation-delay: 0.6s;
}

.feature-card:nth-child(7) {
  animation-delay: 0.7s;
}

.feature-icon {
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, var(--card-color), transparent);
  border: none;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  position: relative;
  transition: all 0.3s ease;
}

.feature-card:hover .feature-icon {
  transform: scale(1.1) rotate(5deg);
}

.feature-icon::before {
  content: '';
  position: absolute;
  inset: 0;
  background: var(--card-color);
  opacity: 0.1;
  border-radius: 20px;
}

.feature-icon i {
  font-size: 1.8rem;
  color: var(--card-color);
  position: relative;
  z-index: 1;
  filter: brightness(0.8);
}

.feature-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--light-text);
  margin-bottom: 0.75rem;
}

.feature-description {
  font-size: 0.95rem;
  color: var(--light-text-muted);
  line-height: 1.6;
  margin: 0;
}

/* Call to Action */
.welcome-cta {
  text-align: center;
  animation: fadeInUp 0.8s ease-out 0.8s both;
}

.cta-button {
  min-height: 60px;
  padding: 1rem 4rem;
  font-size: 1.2rem;
  font-weight: 700;
  border-radius: 50px;
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, #36A2EB, #4BC0C0);
  border: none;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 8px 30px rgba(54, 162, 235, 0.4);
}

.cta-button:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 40px rgba(54, 162, 235, 0.5);
}

.cta-button:active {
  transform: translateY(-1px);
}

.button-content {
  position: relative;
  z-index: 1;
}

.button-shine {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transition: left 0.5s ease;
}

.cta-button:hover .button-shine {
  left: 100%;
}

.cta-note {
  font-size: 0.95rem;
  color: var(--light-text-muted);
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem 0 1rem 0;
}

.cta-note i {
  color: #36A2EB;
  font-size: 1.1rem;
}

/* Responsive Design */
@media (max-width: 768px) {
  .auth-required {
    padding: 1rem;
  }

  .welcome-hero {
    margin-bottom: 2rem;
    padding: 3rem 1rem;
  }

  .gradient-orb {
    filter: blur(60px);
  }

  .orb-1 {
    width: 300px;
    height: 300px;
  }

  .orb-2 {
    width: 250px;
    height: 250px;
  }

  .orb-3 {
    width: 200px;
    height: 200px;
  }

  .app-logo {
    width: 90px;
    height: 90px;
    border-radius: 25px;
  }

  .app-logo i {
    font-size: 2.5rem;
  }

  .welcome-title {
    font-size: 2.5rem;
  }

  .welcome-subtitle {
    font-size: 1.1rem;
  }

  .hero-stats {
    gap: 1rem;
  }

  .stat-item {
    padding: 0.6rem 1.2rem;
    font-size: 0.85rem;
  }

  .mission-statement {
    margin-bottom: 2rem;
  }

  .mission-content {
    padding: 3rem 2rem;
  }

  .mission-content::before {
    filter: blur(15px);
  }

  .mission-title {
    font-size: 1.75rem;
    margin-bottom: 1.5rem;
  }

  .mission-title::after {
    width: 60px;
    height: 3px;
    margin-top: 1rem;
  }

  .mission-text {
    font-size: 1rem;
    line-height: 1.8;
    padding: 0 1rem;
  }

  .mission-text::before,
  .mission-text::after {
    font-size: 3rem;
  }

  .mission-text::before {
    top: -15px;
    left: -10px;
  }

  .mission-text::after {
    bottom: -30px;
    right: -10px;
  }

  .features-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 2rem;
  }

  .feature-card {
    padding: 1.5rem;
    border-radius: 15px;
  }

  .feature-icon {
    width: 60px;
    height: 60px;
    border-radius: 15px;
  }

  .feature-icon i {
    font-size: 1.5rem;
  }

  .feature-title {
    font-size: 1.15rem;
  }

  .feature-description {
    font-size: 0.9rem;
  }

  .cta-button {
    width: 100%;
    padding: 1rem 2rem;
    font-size: 1.1rem;
  }
}

/* Dark Mode Support */
:global([data-theme="dark"]) .app-logo {
  box-shadow: 0 20px 60px rgba(54, 162, 235, 0.6);
}

:global([data-theme="dark"]) .stat-item {
  background: linear-gradient(135deg, rgba(54, 162, 235, 0.15), rgba(75, 192, 192, 0.15));
}

:global([data-theme="dark"]) .stat-item::before {
  background: linear-gradient(135deg, rgba(54, 162, 235, 0.25), rgba(75, 192, 192, 0.25));
}

:global([data-theme="dark"]) .feature-card {
  box-shadow: none;
}

:global([data-theme="dark"]) .feature-card:hover {
  box-shadow: none;
}

:global([data-theme="dark"]) .cta-button {
  box-shadow: 0 8px 30px rgba(54, 162, 235, 0.5);
}

:global([data-theme="dark"]) .cta-button:hover {
  box-shadow: 0 12px 40px rgba(54, 162, 235, 0.6);
}

/* Animation */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>